"use client";

import { getTypedChildren } from "@faire/slate/lib/getTypedChildren";
import { componentGroupCls } from "@faire/slate/lib/tracking";
import { Fieldset } from "@faire/slate/private/form/Fieldset";
import type { FormGroupLabelVariant } from "@faire/slate/private/form/types";
import { buildFormIds } from "@faire/slate/private/form/utils";
import type { IText } from "@faire/slate/private/shared/types";
import React, { PropsWithChildren, useId } from "react";

import { Checkbox } from "./Checkbox";
import { CHECKBOX_GROUP_UNIQUE_ID_PREFIX } from "./consts";

type IProps = {
  /**
   * Whether all checkboxes in the checkbox group are disabled.
   */
  disabled?: boolean;

  /**
   * Controls the error state of the checkbox group.
   */
  error?: IText;

  /**
   * The checkbox group header label.
   */
  label?: string;

  /**
   * The font weight of the checkbox group header label.
   *
   * @default "regular"
   */
  labelVariant?: FormGroupLabelVariant;

  /**
   * **web**
   * The ref of the checkbox group.
   */
  ref?: React.Ref<HTMLDivElement>;

  // DO NOT ADD any new prop without aligning with Slate team first
};

/**
 * CheckboxGroups enable users to select one or more items from a list.
 *
 */
export const CheckboxGroup: React.FC<PropsWithChildren<IProps>> = ({
  children,
  disabled,
  error,
  label,
  labelVariant = "regular",
  ...props
}) => {
  const [{ checkboxes }, rest] = getTypedChildren(children, {
    checkboxes: Checkbox,
  });

  if (rest.length > 0) {
    // oxlint-disable-next-line no-console
    console.error("CheckboxGroup accepts only Checkbox");
  }

  const { helperId } = buildFormIds({
    prefix: CHECKBOX_GROUP_UNIQUE_ID_PREFIX,
    label,
    error: error ? true : undefined,
  });

  const baseGroupId = useId();
  const uniqueGroupId = `${CHECKBOX_GROUP_UNIQUE_ID_PREFIX}-${baseGroupId}`;

  const shouldShowLabel = label;

  return (
    <Fieldset
      className={componentGroupCls("fs-checkbox-group")}
      data-component-name="Slate/CheckboxGroup"
      disabled={disabled}
      error={error}
      gap={4}
      label={shouldShowLabel ? label : undefined}
      labelVariant={labelVariant}
      helperId={helperId}
      optional={false}
      {...props}
    >
      <div className="flex flex-col gap-(--slate-spacing-element-medium)">
        {checkboxes.map((checkbox, index) => {
          // Use the element's own key for stability across list reorders/insertions/removals.
          // Falling back to index only when no key is present (which should never happen in practice).
          const stableIdentifier =
            checkbox.key != null ? String(checkbox.key) : String(index);
          const uniqueId = `${uniqueGroupId}-${stableIdentifier}`;
          return React.cloneElement(checkbox, {
            disabled: disabled ?? checkbox.props.disabled,
            id: uniqueId,
            key: uniqueId,
          });
        })}
      </div>
    </Fieldset>
  );
};
